import Vue from 'vue'
import VueRouter from 'vue-router'
import Layout from '@/layout/index.vue'
import NProgress from 'nprogress'
import { getToken } from '@/utils/auth'
import store from '@/store'
import { isSessionExpired } from '@/utils/session'

Vue.use(VueRouter)

const routes = [
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/views/login/index.vue'),
    meta: { title: '登录' }
  },
  {
    path: '/scada/fullscreen-device/:id',
    name: 'ScadaFullscreenDevice',
    component: () => import('@/views/scada/ViewDeviceScada.vue'),
    meta: { title: '设备组态监控' }
  },
  {
    path: '/energy/report/fullscreen',
    name: 'EnergyReportFullscreen',
    component: () => import('@/views/energy/Report.vue'),
    meta: { title: '设备抄表报表（全屏）' }
  },
  {
    path: '/',
    component: Layout,
    redirect: '/home',
    children: [
      // ── 首页 ──
      {
        path: '/home',
        name: 'Home',
        component: () => import('@/views/home/Dashboard.vue'),
        meta: { title: '首页', icon: 'el-icon-s-home', group: '首页', permission: 'CarbonManagement.Dashboard' }
      },

      // ── 组态监控 ──
      {
        path: '/scada/list',
        name: 'ScadaList',
        component: () => import('@/views/scada/List.vue'),
        meta: { title: '组态监控', icon: 'el-icon-data-board', group: '组态监控', permission: 'CarbonManagement.Scada' }
      },
      {
        path: '/scada/view-device/:id',
        name: 'ScadaViewDevice',
        component: () => import('@/views/scada/ViewDeviceScada.vue'),
        meta: { title: '设备组态监控', group: '组态监控', hidden: true, permission: 'CarbonManagement.Scada' }
      },
      {
        path: '/scada/view-device-2d/:id',
        name: 'ScadaViewDevice2D',
        component: () => import('@/views/scada/ViewDeviceScada2D.vue'),
        meta: { title: '设备组态监控(2D)', group: '组态监控', hidden: true, permission: 'CarbonManagement.Scada' }
      },

      // ── 实时监控 ──
      {
        path: '/monitoring/realtime',
        name: 'RealTimeMonitor',
        component: () => import('@/views/monitoring/RealTimeMonitor.vue'),
        meta: { title: '实时监控', icon: 'el-icon-monitor', group: '实时监控', permission: 'CarbonManagement.Monitoring' }
      },

      // ── 能源管理 ──
      {
        path: '/energy/sub-item',
        name: 'EnergySubItem',
        component: () => import('@/views/energy/SubItem.vue'),
        meta: { title: '分项用能', icon: 'el-icon-pie-chart', group: '能源管理', permission: 'CarbonManagement.Energy' }
      },
      {
        path: '/energy/history',
        name: 'EnergyHistory',
        component: () => import('@/views/energy/History.vue'),
        meta: { title: '历史明细', icon: 'el-icon-time', group: '能源管理', permission: 'CarbonManagement.Energy' }
      },
      {
        path: '/energy/time-of-use',
        name: 'TimeOfUse',
        component: () => import('@/views/energy/TimeOfUse.vue'),
        meta: { title: '分时电量', icon: 'el-icon-data-line', group: '能源管理', permission: 'CarbonManagement.Energy' }
      },
      {
        path: '/energy/compare',
        name: 'EnergyCompare',
        component: () => import('@/views/energy/Compare.vue'),
        meta: { title: '用能对比', icon: 'el-icon-sort', group: '能源管理', permission: 'CarbonManagement.Energy' }
      },
      {
        path: '/energy/benchmark',
        name: 'EnergyBenchmark',
        component: () => import('@/views/energy/Benchmark.vue'),
        meta: { title: '能效对标', icon: 'el-icon-trophy', group: '能源管理', permission: 'CarbonManagement.Energy' }
      },
      {
        path: '/energy/power-quality',
        name: 'PowerQuality',
        component: () => import('@/views/energy/PowerQuality.vue'),
        meta: { title: '电能质量分析', icon: 'el-icon-lightning', group: '能源管理', permission: 'CarbonManagement.Energy' }
      },
      {
        path: '/energy/budget',
        name: 'EnergyBudget',
        component: () => import('@/views/energy/Budget.vue'),
        meta: { title: '用能与碳排预算', icon: 'el-icon-wallet', group: '能源管理', permission: 'CarbonManagement.Energy' }
      },
      {
        path: '/energy/config',
        name: 'EnergyConfig',
        component: () => import('@/views/energy/Config.vue'),
        meta: { title: '用能计算配置', icon: 'el-icon-setting', group: '能源管理', permission: 'CarbonManagement.Energy' }
      },

      // ── 报警管理 ──
      {
        path: '/alarm/real-time',
        name: 'AlarmRealTime',
        component: () => import('@/views/alarm/RealTime.vue'),
        meta: { title: '实时报警', icon: 'el-icon-bell', group: '报警管理', permission: 'CarbonManagement.Alarm' }
      },
      {
        path: '/alarm/threshold',
        name: 'AlarmThreshold',
        component: () => import('@/views/alarm/ThresholdConfig.vue'),
        meta: { title: '报警阈值配置', icon: 'el-icon-set-up', group: '报警管理', permission: 'CarbonManagement.Alarm' }
      },
      {
        path: '/alarm/mask',
        name: 'AlarmMask',
        component: () => import('@/views/alarm/Mask.vue'),
        meta: { title: '报警屏蔽', icon: 'el-icon-turn-off', group: '报警管理', permission: 'CarbonManagement.Alarm' }
      },
      {
        path: '/alarm/report',
        name: 'AlarmReport',
        component: () => import('@/views/alarm/Report.vue'),
        meta: { title: '报警报表', icon: 'el-icon-data-analysis', group: '报警管理', permission: 'CarbonManagement.Alarm' }
      },
      {
        path: '/alarm/work-order',
        name: 'AlarmWorkOrder',
        component: () => import('@/views/alarm/WorkOrder.vue'),
        meta: { title: '报警工单', icon: 'el-icon-tickets', group: '报警管理', permission: 'CarbonManagement.Alarm' }
      },

      // ── 碳管理 ──
      {
        path: '/carbon/factor',
        name: 'EmissionFactor',
        component: () => import('@/views/carbon/Factor.vue'),
        meta: { title: '碳排因子库', icon: 'el-icon-discount', group: '碳管理', permission: 'CarbonManagement.Carbon' }
      },
      {
        path: '/carbon/org-entity',
        name: 'OrgInventoryEntity',
        component: () => import('@/views/carbon/OrgEntity.vue'),
        meta: { title: '核算主体', icon: 'el-icon-office-building', group: '碳管理', permission: 'CarbonManagement.Carbon' }
      },
      {
        path: '/carbon/org-inventory',
        name: 'OrgInventory',
        component: () => import('@/views/carbon/OrgInventory.vue'),
        meta: { title: '组织碳盘查', icon: 'el-icon-document-checked', group: '碳管理', permission: 'CarbonManagement.Carbon' }
      },
      {
        path: '/carbon/org-inventory/:id',
        name: 'OrgInventoryDetail',
        component: () => import('@/views/carbon/OrgInventoryDetail.vue'),
        meta: { title: '盘查明细', group: '碳管理', hidden: true, permission: 'CarbonManagement.Carbon' }
      },
      {
        path: '/carbon/product',
        name: 'CarbonProduct',
        component: () => import('@/views/carbon/Product.vue'),
        meta: { title: '产品管理', icon: 'el-icon-goods', group: '碳管理', permission: 'CarbonManagement.Carbon' }
      },
      {
        path: '/carbon/footprint',
        name: 'ProductFootprint',
        component: () => import('@/views/carbon/Footprint.vue'),
        meta: { title: '产品碳足迹', icon: 'el-icon-orange', group: '碳管理', permission: 'CarbonManagement.Carbon' }
      },
      {
        path: '/carbon/footprint/:id',
        name: 'FootprintDetail',
        component: () => import('@/views/carbon/FootprintDetail.vue'),
        meta: { title: '足迹明细', group: '碳管理', hidden: true, permission: 'CarbonManagement.Carbon' }
      },
      {
        path: '/carbon/footprint-accounting',
        name: 'FootprintAccounting',
        component: () => import('@/views/carbon/FootprintAccounting.vue'),
        meta: { title: '碳足迹核算', icon: 'el-icon-data-analysis', group: '碳管理', permission: 'CarbonManagement.Carbon' }
      },
      {
        path: '/carbon/supply-chain',
        name: 'SupplyChain',
        component: () => import('@/views/carbon/SupplyChain.vue'),
        meta: { title: '供应链碳管理', icon: 'el-icon-share', group: '碳管理', permission: 'CarbonManagement.Carbon' }
      },
      {
        path: '/carbon/asset',
        name: 'CarbonAsset',
        component: () => import('@/views/carbon/CarbonAsset.vue'),
        meta: { title: '碳资产管理', icon: 'el-icon-coin', group: '碳管理', permission: 'CarbonManagement.Carbon' }
      },
      {
        path: '/carbon/verification',
        name: 'CarbonVerification',
        component: () => import('@/views/carbon/Verification.vue'),
        meta: { title: '碳核查支撑', icon: 'el-icon-files', group: '碳管理', permission: 'CarbonManagement.Carbon' }
      },

      // ── 报表管理 ──
      {
        path: '/report/energy-usage',
        name: 'EnergyUsageReport',
        component: () => import('@/views/report/EnergyUsageReport.vue'),
        meta: { title: '用能报表', icon: 'el-icon-document', group: '报表管理', permission: 'CarbonManagement.Report' }
      },
      {
        path: '/report/analysis',
        name: 'ReportAnalysis',
        component: () => import('@/views/report/Analysis.vue'),
        meta: { title: '分析报表', icon: 'el-icon-data-analysis', group: '报表管理', permission: 'CarbonManagement.Report' }
      },
      {
        path: '/report/operation',
        name: 'OperationAnalysis',
        component: () => import('@/views/report/OperationAnalysis.vue'),
        meta: { title: '操作报表', icon: 'el-icon-s-operation', group: '报表管理', permission: 'CarbonManagement.Report' }
      },
      {
        path: '/report/inspection',
        name: 'InspectionReport',
        component: () => import('@/views/report/InspectionReport.vue'),
        meta: { title: '巡检报表', icon: 'el-icon-document-checked', group: '报表管理', permission: 'CarbonManagement.Report' }
      },

      // ── 设备管理 ──
      {
        path: '/device/type',
        name: 'DeviceType',
        component: () => import('@/views/device/Type.vue'),
        meta: { title: '设备类型管理', icon: 'el-icon-s-operation', group: '设备管理', permission: 'CarbonManagement.Device' }
      },
      {
        path: '/device/list',
        name: 'DeviceList',
        component: () => import('@/views/device/List.vue'),
        meta: { title: '设备台账', icon: 'el-icon-cpu', group: '设备管理', permission: 'CarbonManagement.Device' }
      },
      {
        path: '/device/project',
        name: 'ProjectManage',
        component: () => import('@/views/device/Project.vue'),
        meta: { title: '项目维护', icon: 'el-icon-office-building', group: '设备管理', permission: 'CarbonManagement.Device' }
      },
      {
        path: '/device/estimation-config',
        name: 'DeviceEstimationConfig',
        component: () => import('@/views/device/EstimationConfig.vue'),
        meta: { title: '设备估算参数', icon: 'el-icon-odometer', group: '设备管理', permission: 'CarbonManagement.Device' }
      },

      // ── 系统管理 ──
      {
        path: '/system/user',
        name: 'UserManage',
        component: () => import('@/views/system/User.vue'),
        meta: { title: '用户管理', icon: 'el-icon-user', group: '系统管理', permission: 'AbpIdentity.Users' }
      },
      {
        path: '/system/role',
        name: 'RoleManage',
        component: () => import('@/views/system/Role.vue'),
        meta: { title: '角色管理', icon: 'el-icon-s-check', group: '系统管理', permission: 'AbpIdentity.Roles' }
      },
      {
        path: '/system/electricity-price-config',
        name: 'ElectricityPriceConfig',
        component: () => import('@/views/system/ElectricityPriceConfig.vue'),
        meta: { title: '电价配置', icon: 'el-icon-coin', group: '系统管理', permission: 'CarbonManagement.System' }
      },
      {
        path: '/system/audit-log',
        name: 'AuditLog',
        component: () => import('@/views/system/AuditLog.vue'),
        meta: { title: '操作日志', icon: 'el-icon-document-copy', group: '系统管理', permission: 'CarbonManagement.System.AuditLog' }
      },

      // ── 个人中心（不显示在侧边栏） ──
      {
        path: '/account/profile',
        name: 'Profile',
        component: () => import('@/views/account/Profile.vue'),
        meta: { title: '个人中心', hidden: true }
      }
    ]
  }
]

const router = new VueRouter({
  mode: 'history',
  routes
})

/**
 * 找出当前账号第一个有权访问的菜单页。
 * 权限受限的账号登录后若硬跳 /home 会被守卫拦下，体验上像是登录失败，
 * 因此改为落到它确实能看的第一个页面；一个都没有时退到个人中心。
 */
function firstAllowedPath() {
  const layout = routes.find(r => r.path === '/')
  const hit = (layout?.children || []).find(
    c => c.meta && !c.meta.hidden && c.meta.group && store.getters.hasPermission(c.meta.permission)
  )
  return hit ? hit.path : '/account/profile'
}

// 路由守卫
const whiteList = ['/login']
router.beforeEach(async (to, from, next) => {
  NProgress.start()
  document.title = to.meta.title ? `${to.meta.title} - 君品集团能碳智能管理平台` : '君品集团能碳智能管理平台'

  if (getToken()) {
    if (to.path === '/login') {
      next({ path: '/' })
      return
    }
    if (!store.getters.userInfo) {
      try {
        await store.dispatch('user/getInfo')
      } catch (e) {
        if (isSessionExpired(e)) {
          await store.dispatch('user/logout')
          next({ path: '/login', query: { redirect: to.fullPath } })
        } else {
          NProgress.done()
          Vue.prototype.$message.error('页面加载失败，请稍后刷新重试')
          next(false)
        }
        return
      }
    }

    // 菜单过滤只是隐藏入口，直接敲 URL 仍需在这里拦一道
    const required = to.meta && to.meta.permission
    if (required && !store.getters.hasPermission(required)) {
      NProgress.done()
      const fallback = firstAllowedPath()
      // 登录后落到默认首页被改派时不提示：那不是用户主动点的，弹错会让人以为登录失败
      const fromLogin = !from.name || from.path === '/login'
      if (!fromLogin) {
        Vue.prototype.$message && Vue.prototype.$message.error('没有访问该页面的权限')
      }
      next(from.path === fallback ? false : fallback)
      return
    }

    next()
  } else {
    if (whiteList.includes(to.path)) {
      next()
    } else {
      next(`/login?redirect=${to.path}`)
    }
  }
})

router.afterEach(() => {
  NProgress.done()
})

export default router
